import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, Info, Check, Calendar, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { getAvailableQuarterlyVersions, taskEnqueue } from '@/utils/taskHelpers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

interface VersionInfo {
  version: string;
  quarter: string;
  date: string;
  hasPlans: boolean;
}

interface ExtractMetricsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  subslug?: string | null;
}

const ExtractMetricsModal: React.FC<ExtractMetricsModalProps> = ({
  isOpen,
  onOpenChange,
  slug,
  subslug
}) => {
  const { toast } = useToast();
  const supabase = useSupabaseClient();
  const [force, setForce] = useState(false);
  const [associationsOnly, setAssociationsOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [versionInfos, setVersionInfos] = useState<VersionInfo[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [results, setResults] = useState<{ taskIds: string[], versions: string[] } | null>(null);

  // Fetch available versions when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, slug, subslug]);

  const fetchVersions = async () => {
    setIsLoading(true);
    try {
      const versionsData = await getAvailableQuarterlyVersions(slug, subslug);
      setVersionInfos(versionsData);
      
      // Select the latest version by default (first one in the array)
      if (versionsData.length > 0) {
        setSelectedVersions([versionsData[0].version]);
      } else {
        setSelectedVersions([]);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast({
        title: "Error",
        description: "Failed to load available versions.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVersionToggle = (version: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(version)) {
        return prev.filter(v => v !== version);
      } else {
        return [...prev, version];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedVersions.length === versionInfos.filter(v => v.hasPlans).length) {
      // If all are selected, deselect all
      setSelectedVersions([]);
    } else {
      // Otherwise, select all versions with plans
      setSelectedVersions(versionInfos.filter(v => v.hasPlans).map(v => v.version));
    }
  };

  const handleSubmit = async () => {
    if (selectedVersions.length === 0) {
      toast({
        title: "No versions selected",
        description: "Please select at least one version to process.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setResults(null);

    try {
      const taskIds: string[] = [];
      const successVersions: string[] = [];

      for (const version of selectedVersions) {
        try {
          // Directly use the task type based on the toggle selection
          const taskType = associationsOnly ? 'ASSOCIATE_METRICS' : 'EXTRACT_METRICS';
          
          // Use the correct taskEnqueue signature (taskType, slug, options)
          const result = await taskEnqueue(
            taskType, // This will be passed directly to the task-enqueue function
            slug,
            {
              version: version,
              force: associationsOnly ? false : force, // Force doesn't apply to associations
              subslug: subslug || undefined
            }
          );

          if (result.error) {
            console.error(`Error enqueueing task for version ${version}:`, result.error);
            continue;
          }

          if (result.task_id) {
            taskIds.push(result.task_id);
            successVersions.push(version);
          }
        } catch (error) {
          console.error(`Error processing version ${version}:`, error);
        }
      }

      setResults({
        taskIds,
        versions: successVersions
      });
      
      if (taskIds.length === 0) {
        toast({
          title: "No tasks created",
          description: "Failed to create any tasks. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Tasks created",
          description: `Created ${taskIds.length} ${associationsOnly ? 'association' : 'metrics extraction'} ${taskIds.length === 1 ? 'task' : 'tasks'}.`,
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error creating tasks:', error);
      toast({
        title: "Error",
        description: "Failed to create tasks.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset states when closing
    if (results) {
      setResults(null);
      setSelectedVersions([]);
      setForce(false);
      setAssociationsOnly(false);
    }
  };

  const formatDate = (version: string) => {
    try {
      // Format the date from version string like "2023Q1"
      const year = version.substring(0, 4);
      return year;
    } catch {
      return version;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Extract Metrics</DialogTitle>
          <DialogDescription>
            Select quarterly versions to run metrics extraction or association tasks.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Toggles section */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="select-all"
                checked={selectedVersions.length === versionInfos.filter(v => v.hasPlans).length && versionInfos.length > 0}
                onCheckedChange={handleSelectAll}
                disabled={versionInfos.length === 0 || isLoading}
              />
              <Label htmlFor="select-all">Select All</Label>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSelectAll}
              disabled={versionInfos.length === 0 || isLoading}
              className="text-xs"
            >
              {selectedVersions.length === versionInfos.filter(v => v.hasPlans).length && versionInfos.length > 0
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>

          {/* Force override toggle */}
          <div className="flex items-center justify-between py-2 mb-2">
            <div className="space-y-0.5">
              <Label htmlFor="force">Force Re-Extract</Label>
              <span className="text-xs text-muted-foreground">
                Override existing metrics data
              </span>
            </div>
            <Switch
              id="force"
              checked={force}
              onCheckedChange={setForce}
              disabled={associationsOnly}
            />
          </div>

          {/* Associations only toggle */}
          <div className="flex items-center justify-between py-2 mb-4">
            <div className="space-y-0.5">
              <Label htmlFor="associations-only">Run Associations Only</Label>
              <span className="text-xs text-muted-foreground">
                Only run metrics association without extraction
              </span>
            </div>
            <Switch
              id="associations-only"
              checked={associationsOnly}
              onCheckedChange={(checked) => {
                setAssociationsOnly(checked);
                if (checked) {
                  // Disable force when associations only is selected
                  setForce(false);
                }
              }}
            />
          </div>

          {/* Force alerts */}
          {!force && !associationsOnly && (
            <Alert variant="warning" className="bg-yellow-50 border-yellow-100 mb-4">
              <AlertCircle className="h-4 w-4 text-yellow-800" />
              <AlertDescription className="text-yellow-800 text-xs">
                Only missing metrics will be extracted. Enable "Force Re-Extract" to override existing data.
              </AlertDescription>
            </Alert>
          )}

          {/* Associations only info */}
          {associationsOnly && (
            <Alert className="bg-blue-50 border-blue-100 mb-4">
              <Info className="h-4 w-4 text-blue-800" />
              <AlertDescription className="text-blue-800 text-xs">
                Only the association between metrics and plans/charges will be processed. No new metrics will be extracted.
              </AlertDescription>
            </Alert>
          )}

          {/* Version selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Versions</Label>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : versionInfos.length === 0 ? (
              <Alert className="bg-gray-50 border-gray-100">
                <Info className="h-4 w-4 text-gray-600" />
                <AlertDescription className="text-gray-800">
                  No quarterly versions found for this company.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="max-h-60 overflow-y-auto border rounded-md">
                {versionInfos.map((versionInfo) => (
                  <div 
                    key={versionInfo.version} 
                    className={`flex items-center p-2 border-b last:border-b-0 ${!versionInfo.hasPlans ? 'bg-gray-50 opacity-70' : ''}`}
                  >
                    <Checkbox 
                      id={`version-${versionInfo.version}`}
                      checked={selectedVersions.includes(versionInfo.version)}
                      onCheckedChange={() => handleVersionToggle(versionInfo.version)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={`version-${versionInfo.version}`} 
                        className={`flex flex-col cursor-pointer ${!versionInfo.hasPlans ? 'text-gray-500' : ''}`}
                      >
                        <span className="font-medium text-sm flex items-center">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          {versionInfo.quarter} <span className="text-gray-500 ml-1.5 text-xs">({formatDate(versionInfo.version)})</span>
                        </span>
                        {!versionInfo.hasPlans && (
                          <span className="text-xs text-blue-400 flex items-center mt-0.5">
                            <Info className="h-3 w-3 mr-1" />
                            No plans data available (informational only)
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Results section */}
          {results && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium mb-2">Task Results</h4>
              {results.taskIds.length === 0 ? (
                <p className="text-sm text-gray-600">No tasks were created.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    Created {results.taskIds.length} tasks for the following versions:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {results.versions.map((version) => (
                      <li key={version} className="flex items-center">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        {version}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {results ? "Close" : "Cancel"}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || selectedVersions.length === 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Tasks...
              </>
            ) : (
              associationsOnly ? "Run Associations" : "Run Extracts"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExtractMetricsModal;
